import { createFileRoute, useRouter } from "@tanstack/react-router";
import { useMemo, useState, useEffect, useCallback, Activity } from "react";
import { useSetAtom } from "jotai";
import { championshipTourModeAtom } from "@/lib/gameStatusAtom";
import { useNextStep } from "@/components/NextStepProvider";
import { FloatingLeftButton } from "@/components/FloatingLeftButton";
import ChampionshipCard from "@/components/ChampionshipCard";
import { DefaultButton } from "@/components/DefaultButton";
import { ScrollArea } from "@/components/ui/scroll-area";
import { WithPullToRefresh } from "@/components/ui/pull-to-refresh";
import { useChampionshipList, useTheme, useStats } from "@/lib/hooks/useApi";
import type { ChampionshipResponse } from "@/lib/types/api";
import { Spinner } from "@/components/ui/spinner";
import { generateTextShadow } from "@/lib/utils";
import useUserAgent from "@/lib/hooks/useUserAgent";
import { usePrivy } from "@privy-io/react-auth";
import { t } from "@/lib/i18n";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { HowItWorkButton } from "@/components/ui/HowItWorkButton";
import ResourceBar from "@/components/ResourceBar";
import { ShinyWavyText } from "@/components/ShinyWavyText";
import { ChampionshipOpen } from "./-components/ChampionshipOpen";
import AnimatedWaiting from "@/components/AnimatedWaiting";
import { TournamentBracketPage } from "./-components/TournamentBracket";

function Championship() {
  const router = useRouter();
  const { isMobile } = useUserAgent();
  const { user } = usePrivy();
  const [roundFilter, setRoundFilter] = useState<number | undefined>(undefined);
  const { data: theme } = useTheme();
  const championshipStatus = theme?.championship;
  const [bracketDialogOpen, setBracketDialogOpen] = useState(
    !championshipStatus?.status.isPopulated,
  );

  const walletAddress = user?.wallet?.address ?? "";
  const { data: statsData } = useStats(walletAddress);
  const gameWalletAddress = statsData?.embeddedWallet ?? "";

  const {
    data,
    isLoading,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
    isError,
    refetch,
  } = useChampionshipList({
    filterByRound: roundFilter,
  });

  const rounds = useMemo(() => {
    const allRounds = data?.pages[0]?.rounds ?? [];
    const maxRound = allRounds.length
      ? Math.max(...allRounds.map((r) => r.round))
      : 0;
    // Filter out "3rd Place" for the last round in the dropdown (only show "Final")
    return allRounds.filter(
      (r) => !(r.round === maxRound && r.roundName === "3rd Place"),
    );
  }, [data]);

  const activeRound = useMemo(() => {
    return rounds.find((round) => round.isActive);
  }, [rounds]);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const availableRounds = useMemo(() => {
    if (!activeRound) return rounds;
    // Only show rounds <= active round (rounds that have started)
    return rounds.filter((round) => round.round <= activeRound.round);
  }, [rounds, activeRound]);

  const championships = useMemo(
    () =>
      data?.pages.flatMap((page) => page.championships) ??
      ([] as ChampionshipResponse[]),
    [data],
  );

  // Set default roundFilter to the active round
  useEffect(() => {
    if (activeRound && roundFilter === undefined) {
      setRoundFilter(activeRound.round);
    }
  }, [activeRound, roundFilter]);

  // Client-side sorting: user's championships (where address is in playerAddresses) first
  // Note: API handles filtering (statusFilter, roundFilter), we only sort here
  const sortedChampionships = useMemo(() => {
    if (!gameWalletAddress) return championships;

    const userChampionships: ChampionshipResponse[] = [];
    const otherChampionships: ChampionshipResponse[] = [];

    championships.forEach((championship) => {
      if (
        championship.playerAddresses?.includes(gameWalletAddress) ||
        championship.playerAddresses?.some(
          (addr) => addr.toLowerCase() === gameWalletAddress.toLowerCase(),
        )
      ) {
        userChampionships.push(championship);
      } else {
        otherChampionships.push(championship);
      }
    });

    return [...userChampionships, ...otherChampionships];
  }, [championships, gameWalletAddress]);

  const { startNextStep, closeNextStep } = useNextStep();
  const setChampionshipTourMode = useSetAtom(championshipTourModeAtom);

  const handleStartChampionshipTour = useCallback(() => {
    try {
      closeNextStep();
    } catch {}
    setChampionshipTourMode(
      championshipStatus?.status.isPopulated ? "bracket" : "qualifier",
    );
    startNextStep("championshipTour");
  }, [
    closeNextStep,
    startNextStep,
    setChampionshipTourMode,
    championshipStatus?.status.isPopulated,
  ]);

  if (!championshipStatus) return;
  if (championshipStatus.status.isActive === false) {
    return <ChampionshipOpen tournamentId="" />;
  }

  const content = (
    <div className="space-y-3">
      {isLoading ? (
        <div className="flex flex-col items-center justify-center gap-2 py-12 text-center text-black/80">
          <AnimatedWaiting
            prefix={t("championship.loading")}
            className="text-sm"
          />
        </div>
      ) : isError ? (
        <div className="flex flex-col items-center justify-center gap-2 py-12 text-center text-red-400">
          <p className="text-sm">{t("championship.loadError")}</p>
        </div>
      ) : sortedChampionships.length ? (
        <>
          {sortedChampionships.map((championship) => (
            <div key={championship.tournamentId} className="w-full">
              <ChampionshipCard championship={championship} rounds={rounds} />
            </div>
          ))}
          {hasNextPage && (
            <div className="flex justify-center pt-2">
              <DefaultButton
                variant="default"
                onClick={() => fetchNextPage()}
                disabled={isFetchingNextPage}
                className="uppercase text-sm font-black"
              >
                {isFetchingNextPage ? (
                  <AnimatedWaiting
                    prefix={t("championship.loadingMore")}
                    className="text-sm"
                  />
                ) : (
                  t("championship.loadMore")
                )}
              </DefaultButton>
            </div>
          )}
          {isFetchingNextPage && (
            <div className="py-4 flex justify-center">
              <Spinner className="w-6 h-6" />
            </div>
          )}
          {!hasNextPage && sortedChampionships.length > 0 && (
            <div className="py-4 text-center text-xs text-black/80">
              {t("championship.endOfList")}
            </div>
          )}
        </>
      ) : (
        <div className="flex flex-col items-center justify-center gap-2 py-12 text-center text-black/80">
          <p className="text-lg font-semibold">
            {t("championship.noChampionshipsFound")}
          </p>
          <p className="text-sm">{t("championship.checkBackLater")}</p>
        </div>
      )}
    </div>
  );

  return (
    <div className="flex flex-1 flex-col h-full overflow-hidden relative">
      <div
        className="absolute top-0 left-0 right-0 h-[25%] pointer-events-none"
        style={{
          backgroundImage: "url(/assets/list-bg-dome.png)",
          backgroundSize: "cover",
          backgroundPosition: "center 3%",
          backgroundRepeat: "no-repeat",
        }}
      />
      <div
        className="absolute bottom-0 left-0 right-0 h-[75%] pointer-events-none bg-[#546C74]"
        style={{
          backgroundImage: "url(/assets/list-bg-dome-2.png)",
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
        }}
      />
      <div className="relative flex flex-col items-center h-full overflow-hidden">
        <FloatingLeftButton
          onClick={() => router.navigate({ to: "/home" })}
          className="absolute left-0 top-4 z-50"
          icon={
            <svg
              className="w-4 h-4"
              viewBox="0 0 9 8"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M3 1V0H2V1H1V2H0V3H1V4H2V5H3V4H6V7H3V8H7V7H8V6H9V3H8V2H7V1H3Z"
                fill="white"
              />
            </svg>
          }
        />
        <ResourceBar
          showBalance={false}
          className="absolute top-4 left-14 right-4 flex z-40 pl-3"
          rightExtra={
            <HowItWorkButton
              onClick={handleStartChampionshipTour}
              size="size-8"
            />
          }
        />

        <div className="w-full max-w-lg flex flex-col h-full relative">
          <div className="flex items-end justify-center h-1/5 pt-6 pb-12 text-center">
            <img
              src={championshipStatus.logo}
              alt={t("championship.altTexts.championship")}
              className="h-full w-auto object-contain"
              style={{ imageRendering: "pixelated" }}
            />
          </div>
          <div className="flex items-center justify-center px-4 -mt-12">
            <p
              className="text-xl text-white text-center uppercase"
              style={{ textShadow: generateTextShadow(1) }}
            >
              {!championshipStatus.status.isPopulated
                ? "QUALIFIER PHASE"
                : (activeRound?.roundName ?? "")}
              {/*{activeRound && `${activeRound!.roundName}`}*/}
            </p>
          </div>
          <div className="flex items-center justify-center px-4">
            {(() => {
              const description = !championshipStatus.status.isPopulated
                ? championshipStatus.mockBracketDescription?.description
                : championshipStatus.startedDescription?.description;
              if (!description) return null;
              const [before, after] = description.split("{prize}");
              return (
                <p className="text-xs text-white/90 text-center">
                  {before && (
                    <span
                      className="me-1"
                      style={{ textShadow: generateTextShadow(0.75) }}
                    >
                      {before.trim()}
                    </span>
                  )}
                  {after !== undefined && (
                    <ShinyWavyText
                      text={championshipStatus.campaignPrize ?? ""}
                      amplitude={-2.25}
                      className="text-xs inline-flex items-baseline"
                    />
                  )}
                  {after && (
                    <span
                      className="ms-1"
                      style={{ textShadow: generateTextShadow(0.75) }}
                    >
                      {after.trim()}
                    </span>
                  )}
                </p>
              );
            })()}
          </div>

          <div className="flex-1 flex flex-col overflow-hidden p-2 pt-6 gap-4">
            <div className="flex flex-col gap-3">
              <div
                data-step="championship-round-filter"
                className="flex items-center justify-center w-full"
              >
                <div className="flex gap-2 items-center flex-wrap w-full">
                  <Select
                    value={roundFilter?.toString()}
                    onValueChange={(value) => {
                      setRoundFilter(parseInt(value, 10));
                    }}
                  >
                    <SelectTrigger
                      size="sm"
                      layout="justify-between"
                      className="h-8 w-full"
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="max-w-[95vw]">
                      {rounds.map((round) => {
                        const isDisabled =
                          activeRound && round.round > activeRound.round;
                        return (
                          <SelectItem
                            key={round.round}
                            value={round.round.toString()}
                            disabled={isDisabled}
                          >
                            {round.roundName}
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            <Activity mode={!bracketDialogOpen ? "visible" : "hidden"}>
              <div className="flex-1 flex flex-col overflow-hidden">
                {isMobile ? (
                  <WithPullToRefresh
                    refetch={refetch}
                    queryKey={[
                      "championshipList",
                      roundFilter !== undefined ? roundFilter.toString() : "",
                    ]}
                    className="flex-1 flex flex-col"
                  >
                    <ScrollArea className="flex-1 min-h-0 pr-1" type="scroll">
                      {content}
                    </ScrollArea>
                  </WithPullToRefresh>
                ) : (
                  <ScrollArea className="flex-1 min-h-0 pr-1" type="scroll">
                    {content}
                  </ScrollArea>
                )}
              </div>
            </Activity>
            <Activity mode={bracketDialogOpen ? "visible" : "hidden"}>
              <TournamentBracketPage />
            </Activity>

            <div className="flex flex-col gap-2 shrink-0">
              {championshipStatus.status.isPopulated && (
                <DefaultButton
                  onClick={() => {
                    setBracketDialogOpen((prev) => !prev);
                    // if (championshipStatus?.metadata.bracketImage) {
                    //   setBracketDialogOpen(true);
                    // }
                  }}
                  variant="default"
                  className="uppercase h-10! text-lg"
                  // disabled={!championshipStatus?.metadata.bracketImage}
                >
                  {bracketDialogOpen
                    ? t("championship.hideBracket")
                    : t("championship.viewBracket")}
                </DefaultButton>
              )}
            </div>
          </div>
        </div>
      </div>

      {/*<BracketDialog
        open={bracketDialogOpen}
        onOpenChange={setBracketDialogOpen}
        bracketImageUrl={championshipStatus?.metadata.bracketImage}
      />*/}
    </div>
  );
}

export const Route = createFileRoute("/(game)/championship/")({
  component: Championship,
});
